import { TextInput, Textarea, Select, Button, Paper, Stack, Group, Text, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconSend, IconCheck, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { treatmentsData } from '../utils/treatmentData';

interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
  treatment: string;
  message: string;
}

export function ContactForm() {
  const [loading, setLoading] = useState(false);
  const theme = useMantineTheme();

  const form = useForm<ContactFormValues>({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      treatment: '',
      message: '',
    },
    validate: {
      name: (value) => (value.trim().length < 2 ? 'Navn skal være mindst 2 tegn' : null),
      email: (value) => (value && !/^\S+@\S+$/.test(value) ? 'Ugyldig email' : null),
      phone: (value) => {
        const digitsOnly = value.replace(/\s|-/g, '');
        return digitsOnly.length < 8 ? 'Telefonnummer skal være mindst 8 cifre' : null;
      },
      message: (value) => (value.trim().length < 10 ? 'Besked skal være mindst 10 tegn' : null),
    },
  });

  const treatmentOptions = [
    { value: '', label: 'Vælg behandling (valgfrit)' },
    ...treatmentsData.map((treatment) => ({
      value: treatment.slug,
      label: `${treatment.title} (${treatment.price} kr)`,
    })),
  ];

  const handleSubmit = async (values: ContactFormValues) => {
    setLoading(true);

    try {
      // Create mailto link with form data
      const subject = `Forespørgsel om ${values.treatment ? treatmentsData.find(t => t.slug === values.treatment)?.title || 'akupunktur behandling' : 'akupunktur behandling'}`;
      const body = `Navn: ${values.name}
Email: ${values.email}
Telefon: ${values.phone}
${values.treatment ? `Behandling: ${treatmentsData.find(t => t.slug === values.treatment)?.title}` : ''}

Besked:
${values.message}`;

      const mailtoLink = `mailto:lykketerapien@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

      // Open mailto link
      window.location.href = mailtoLink;

      // Show success notification
      notifications.show({
        title: 'Email klient åbnet',
        message: 'Din email klient skulle nu åbne med beskeden udfyldt. Send emailen for at kontakte os.',
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 6000,
      });

      // Reset form
      form.reset();
    } catch (error) {
      notifications.show({
        title: 'Fejl',
        message: 'Der opstod en fejl. Prøv venligst igen eller ring direkte på +45 60 80 37 35.',
        color: 'red',
        icon: <IconX size={18} />,
        autoClose: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper shadow="sm" p="xl" radius="md" withBorder className="contact-form-paper">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Text size="lg" fw={600} mb="sm" className="contact-form-title">
            Send mig en besked
          </Text>

          <TextInput
            label="Navn"
            placeholder="Dit fulde navn"
            required
            {...form.getInputProps('name')}
          />

          <TextInput
            label="Email"
            placeholder="din@email.dk"
            type="email"
            {...form.getInputProps('email')}
          />

          <TextInput
            label="Telefon"
            placeholder="30 16 72 62"
            type="tel"
            required
            {...form.getInputProps('phone')}
          />

          <Select
            label="Ønsket behandling"
            placeholder="Vælg behandling"
            data={treatmentOptions}
            searchable
            clearable
            {...form.getInputProps('treatment')}
          />

          <Textarea
            label="Besked"
            placeholder="Beskriv dine behov eller stil spørgsmål..."
            required
            minRows={5}
            {...form.getInputProps('message')}
          />

          <Group justify="space-between" mt="md" className="contact-form-footer">
            <Text size="sm" c="dimmed" className="contact-form-response-time">
              Jeg svarer typisk inden for 24 timer
            </Text>
            <Button
              type="submit"
              loading={loading}
              leftSection={<IconSend size={18} />}
              color="gold"
              size="md"
              className="contact-form-submit"
            >
              Send besked
            </Button>
          </Group>
        </Stack>
      </form>

      <style>{`
        /* Mobile-first responsive design */
        @media (max-width: 768px) {
          .contact-form-paper {
            padding: 1.5rem !important;
          }

          .contact-form-title {
            font-size: 1.25rem !important;
            text-align: center;
            margin-bottom: 1rem !important;
          }

          .contact-form-footer {
            flex-direction: column !important;
            gap: 1rem !important;
            align-items: stretch !important;
          }

          .contact-form-response-time {
            text-align: center;
            order: 2;
          }

          .contact-form-submit {
            width: 100% !important;
            order: 1;
            height: 48px !important;
            font-size: 1rem !important;
          }

          .mantine-TextInput-root,
          .mantine-Select-root,
          .mantine-Textarea-root {
            margin-bottom: 0.5rem;
          }

          .mantine-TextInput-label,
          .mantine-Select-label,
          .mantine-Textarea-label {
            font-size: 0.95rem !important;
            margin-bottom: 0.5rem !important;
          }

          .mantine-TextInput-input,
          .mantine-Select-input,
          .mantine-Textarea-input {
            font-size: 16px !important; /* Prevents iOS zoom */
            min-height: 48px !important; /* Better touch target */
          }

          .mantine-Textarea-input {
            min-height: 120px !important;
          }
        }

        /* Tablet */
        @media (min-width: 769px) and (max-width: 1024px) {
          .contact-form-paper {
            padding: 2rem !important;
          }
        }
      `}</style>
    </Paper>
  );
}
